import fixtures from '../../data/fixtures.json';
import type { Team, Match, Venue } from '../../lib/types.js';
import { Selection, writeTeamsToUrl } from './state.js';
import { showSection } from './router.js';
import { pairTeamsForKnockout, buildOpponentMap, filterPairsByQuery, getActiveKnockoutStage, type GroupedTeams } from './teams-view.js';
import { t, fill, getLang, applyTranslations } from './i18n.js';
import { track } from './analytics.js';

const ALL_MATCHES: Match[] = (fixtures as { matches: Match[] }).matches;
const ALL_VENUES: Venue[] = (fixtures as { venues: Venue[] }).venues;
const ALL_TEAMS: Team[] = (fixtures as { teams: Team[] }).teams;

const ACTIVE_STAGE = getActiveKnockoutStage(ALL_MATCHES);

const ACTIVE_TEAMS: Team[] = (() => {
  const codes = new Set(ALL_MATCHES.filter(m => m.stage === ACTIVE_STAGE).flatMap(m => [m.home, m.away]));
  return ALL_TEAMS.filter(t => codes.has(t.code));
})();

const STAGE_I18N_KEY: Record<string, string> = {
  r32: 'roundOf32', r16: 'roundOf16', qf: 'quarterFinal',
  sf: 'semiFinal', '3rd': 'thirdPlace', final: 'finalRound',
};

function getPaired(): GroupedTeams {
  return pairTeamsForKnockout(ALL_MATCHES, ALL_TEAMS, ALL_VENUES, getLang(), ACTIVE_STAGE);
}

const OPPONENT_MAP = buildOpponentMap(ALL_MATCHES, ACTIVE_STAGE);

function makeTeamBtn(team: Team, selection: Selection, container: HTMLElement): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'team-tile';
  btn.dataset.code = team.code;
  btn.dataset.name = team.name_en;
  btn.setAttribute('aria-pressed', selection.has(team.code) ? 'true' : 'false');

  const flag = document.createElement('span');
  flag.className = 'team-tile-flag';
  flag.setAttribute('aria-hidden', 'true');
  flag.textContent = team.flag_emoji;

  const name = document.createElement('span');
  name.className = 'team-tile-name';
  name.dataset.nameEn = team.name_en;
  name.dataset.nameEs = team.name_es;
  name.dataset.namePt = team.name_pt;
  name.dataset.nameFr = team.name_fr;
  name.dataset.nameAr = team.name_ar;
  name.dataset.nameJa = team.name_ja;
  name.textContent = team[`name_${getLang()}` as keyof typeof team] as string || team.name_en;

  const check = document.createElement('span');
  check.className = 'team-tile-check';
  check.setAttribute('aria-hidden', 'true');
  check.textContent = '✓';

  btn.appendChild(flag);
  btn.appendChild(name);
  btn.appendChild(check);

  btn.addEventListener('click', () => {
    const wasSelected = selection.has(team.code);
    const nowSelected = selection.toggle(team.code);
    btn.setAttribute('aria-pressed', nowSelected ? 'true' : 'false');

    if (!wasSelected) {
      const opponentCode = OPPONENT_MAP.get(team.code);
      if (opponentCode && !selection.has(opponentCode)) {
        selection.toggle(opponentCode);
        const opponentBtn = container.querySelector<HTMLButtonElement>(`.team-tile[data-code="${opponentCode}"]`);
        if (opponentBtn) opponentBtn.setAttribute('aria-pressed', 'true');
      }
    }

    updateMatchSelected(container);

    if (nowSelected) {
      track('team_selected', { team_code: team.code, team_name: team.name_en, selection_size: selection.size() });
    } else {
      track('team_deselected', { team_code: team.code, team_name: team.name_en, selection_size: selection.size() });
    }
  });

  return btn;
}

function updateMatchSelected(container: HTMLElement): void {
  for (const section of Array.from(container.querySelectorAll<HTMLElement>('.match-section'))) {
    const tiles = Array.from(section.querySelectorAll<HTMLButtonElement>('.team-tile'));
    const allSelected = tiles.length === 2 && tiles.every(b => b.getAttribute('aria-pressed') === 'true');
    section.classList.toggle('match-selected', allSelected);
  }
}

function renderGrid(container: HTMLElement, selection: Selection): void {
  container.innerHTML = '';
  for (const { group, teams } of getPaired()) {
    const [dateLabel, venueLabel] = group.split('||');

    const section = document.createElement('div');
    section.className = 'match-section';
    section.dataset.group = group;

    const header = document.createElement('div');
    header.className = 'match-header';

    const dateSpan = document.createElement('span');
    dateSpan.className = 'match-date';
    dateSpan.textContent = dateLabel;

    const venueSpan = document.createElement('span');
    venueSpan.className = 'match-venue';
    venueSpan.textContent = venueLabel;

    header.appendChild(dateSpan);
    header.appendChild(venueSpan);
    section.appendChild(header);

    const grid = document.createElement('div');
    grid.className = 'match-grid';

    const [home, away] = teams;
    grid.appendChild(makeTeamBtn(home, selection, container));

    const vs = document.createElement('div');
    vs.className = 'vs-badge';
    vs.setAttribute('aria-hidden', 'true');
    vs.textContent = 'vs';
    grid.appendChild(vs);

    grid.appendChild(makeTeamBtn(away, selection, container));

    section.appendChild(grid);
    container.appendChild(section);
  }
  updateMatchSelected(container);
}

function applyFilter(container: HTMLElement, query: string): void {
  const filtered = filterPairsByQuery(getPaired(), query, getLang(), OPPONENT_MAP);
  const visibleGroups = new Set(filtered.map((g) => g.group));
  for (const section of Array.from(container.querySelectorAll<HTMLElement>('.match-section'))) {
    const group = section.dataset.group ?? '';
    section.hidden = !visibleGroups.has(group);
  }
}

function renderStickyBar(bar: HTMLElement, codes: readonly string[]): void {
  const count = codes.length;
  bar.dataset.selected = String(count);
  const cta = bar.querySelector<HTMLButtonElement>('#sticky-cta');
  const badge = document.getElementById('cta-badge');
  const label = document.getElementById('cta-label');
  const action = document.getElementById('cta-action') as HTMLElement | null;
  if (!cta) return;
  if (badge) badge.textContent = String(count);
  if (count === 0) {
    if (label) label.textContent = t('ctaEmpty');
    if (action) action.hidden = true;
    cta.disabled = true;
  } else {
    const text = count === 1 ? t('ctaOne') : fill(t('ctaMany'), { n: count });
    if (label) label.textContent = text;
    if (action) action.hidden = false;
    cta.disabled = false;
  }
}

export function mountSelectScreen(selection: Selection): void {
  const grid = document.getElementById('team-grid');
  const search = document.getElementById('team-search') as HTMLInputElement | null;
  const bar = document.getElementById('sticky-bar');
  const cta = document.getElementById('sticky-cta');
  if (!grid || !search || !bar || !cta) return;

  const selectAllBtn = document.getElementById('select-all-btn') as HTMLButtonElement | null;

  function updateSelectAllBtn(): void {
    if (!selectAllBtn) return;
    const allSelected = selection.size() === ACTIVE_TEAMS.length;
    selectAllBtn.textContent = allSelected ? t('clearAll') : t('selectAll');
    selectAllBtn.setAttribute(
      'aria-label',
      allSelected ? t('ariaClearAll') : t('ariaSelectAll'),
    );
    selectAllBtn.dataset.i18n = allSelected ? 'clearAll' : 'selectAll';
  }

  renderGrid(grid, selection);
  renderStickyBar(bar, selection.codes());
  updateSelectAllBtn();

  const roundLabel = document.querySelector<HTMLElement>('.round-label');
  if (roundLabel) {
    roundLabel.dataset.i18n = STAGE_I18N_KEY[ACTIVE_STAGE] ?? 'roundOf32';
    applyTranslations();
  }

  search.addEventListener('input', () => {
    applyFilter(grid, search.value);
  });

  const clearBtn = document.getElementById('search-clear') as HTMLButtonElement | null;
  if (clearBtn) {
    search.addEventListener('input', () => {
      clearBtn.hidden = search.value.length === 0;
      if (selectAllBtn) selectAllBtn.hidden = search.value.length > 0;
    });
    clearBtn.addEventListener('click', () => {
      search.value = '';
      clearBtn.hidden = true;
      if (selectAllBtn) selectAllBtn.hidden = false;
      applyFilter(grid, '');
    });
  }

  if (selectAllBtn) {
    selectAllBtn.addEventListener('click', () => {
      if (selection.size() === ACTIVE_TEAMS.length) {
        selection.clear();
      } else {
        selection.setAll(ACTIVE_TEAMS.map((tm) => tm.code));
      }
      for (const tile of Array.from(grid.querySelectorAll<HTMLButtonElement>('.team-tile'))) {
        const code = tile.dataset.code ?? '';
        tile.setAttribute('aria-pressed', selection.has(code) ? 'true' : 'false');
      }
      updateMatchSelected(grid);
    });
  }

  selection.subscribe((codes) => {
    renderStickyBar(bar, codes);
    writeTeamsToUrl(codes);
    updateSelectAllBtn();
  });

  cta.addEventListener('click', () => {
    track('subscribe_opened', { team_count: selection.size(), teams: selection.codes() });
    showSection('subscribe');
  });
}
